export const findObjectByKeyValue = (array, key, value) => {
    return array?.find(item => item?.[key] === value);
};

export function calculateAge(birthDateString, fallback = null) {
  if (!birthDateString) return fallback;

  const birth = new Date(birthDateString);
  const today = new Date();

  if (Number.isNaN(birth.getTime())) {
    return fallback;
  }

  let age = today.getFullYear() - birth.getFullYear();
  const monthDiff = today.getMonth() - birth.getMonth();

  if (monthDiff < 0 || (monthDiff === 0 && today.getDate() < birth.getDate())) {
    age -= 1;
  }

  return age >= 0 ? age : fallback;
}

export function resolvePlayerAge(profile, fallback = null) {
  const ageFromBirthDate = calculateAge(profile?.birthDate, null);
  if (ageFromBirthDate !== null) {
    return ageFromBirthDate;
  }

  if (profile?.age === undefined || profile?.age === null || profile?.age === '') {
    return fallback;
  }

  const age = Number(profile.age);
  return Number.isFinite(age) && age >= 0 ? Math.trunc(age) : fallback;
}

/**
 * Checks if a given pathname corresponds to a known public route
 * where standard layout elements like Header/Footer might be hidden.
 * @param {string} pathname - The current URL pathname.
 * @returns {boolean} True if the route is considered public, false otherwise.
 */
export function isPublicRoute(pathname) {
  const publicRoutes = [
    '/',                        // Public landing page when logged out
    '/about',
    '/contacts',
    '/contact',
    '/policy',
    '/privacy',
    '/terms',
    '/terms-of-service',
    '/invite',
    '/play',
    '/play-with-friends',
    '/login', 
    '/auth/callback',
    '/register', 
    '/register-personal-info', 
    '/forgot-password',         // Kept this just in case
    '/password-reset-request', 
    '/password-reset' 
  ]; 
  // Checks for exact match or if path starts with a public route followed by '/' 
  return publicRoutes.some(route => pathname === route || pathname.startsWith(route + '/'));
}

/**
 * Formats number to price string with currency and abbreviations
 * @param {number} num - The number to format
 * @param {string} currency - Currency symbol (default "€")
 * @returns {string} Formatted price string (e.g., "€5m", "€3.5m", "€500k")
 */
function formatCompactAmount(amount, divisor, maxDecimals = 2) {
  const multiplier = 10 ** maxDecimals;
  const truncated = Math.trunc((amount * multiplier) / divisor) / multiplier;

  return truncated.toLocaleString("en-US", {
    minimumFractionDigits: 0,
    maximumFractionDigits: maxDecimals
  });
}

export function numToPrice(num, currency = "€") {
  if (!num) return "0";
  if (num >= 1000000) {
    return currency + formatCompactAmount(num, 1000000) + "m";
  } else if (num >= 1000) {
    return currency + formatCompactAmount(num, 1000) + "k";
  } else {
    return currency + num.toString();
  }
}

/**
 * Sorts array by key with specified order
 * Enhanced to handle nested attribute access
 * @param {Array} array - Array to sort
 * @param {string} key - Key to sort by  
 * @param {string} order - Sort order ("asc" or "desc")
 * @param {Object} attributeMapping - Optional mapping for nested attributes
 * @returns {Array} Sorted array
 */
export function sortArrayByKey(array, key, order = "desc", attributeMapping = null) {
  return array.sort((a, b) => {
    let aValue, bValue;
    
    // Handle nested attribute access
    if (attributeMapping && attributeMapping[key]) {
      aValue = a.attributes?.[attributeMapping[key]] || 0;
      bValue = b.attributes?.[attributeMapping[key]] || 0;
    } else {
      // Direct property access for bio fields
      aValue = a[key] ?? 0;
      bValue = b[key] ?? 0;
    }
    
    if (aValue < bValue) {
      return order === "asc" ? -1 : 1;
    }
    if (aValue > bValue) {
      return order === "asc" ? 1 : -1;
    }
    return 0;
  });
}

/**
 * Returns additional CSS class based on column key
 * @param {string} key - Column key
 * @returns {string} CSS class name
 */
export function getColumnClass(key) {
  if (key === "nationality") return "country-col";
  if (key === "position") return "pos-col";
  if (key === "name") return "title-col";
  if (key === "age") return "age-col";
  if (key === "height") return "height-col";
  if (key === "weight") return "weight-col";
  if (key === "value") return "value-col";
  if (key === "signedFor") return "value-col";
  return "";
}

/**
 * Gets short names for player attributes - REUSING EXISTING LOGIC
 * @param {string} attrName - Full attribute name
 * @returns {string} Short attribute name (3 characters)
 */
export function getAttributeShortName(attrName) {
  const shortNames = {
    'Aerial Ability Score': 'AER',
    'Ball Carrying Score': 'CAR', 
    'Ball Retention Score': 'RET',
    'Ball Winning Score': 'WIN',
    'Box Defending Score': 'DEF',
    'Box Movement Score': 'MOV',
    'Buildup Passing Score': 'BLD',
    'Creativity Score': 'CRE',
    'Crossing Score': 'CRO',
    'Defensive Awareness Score': 'AWR',
    'Dribbling Score': 'DRI',
    'Finishing Score': 'FIN',
    'Link-Up Play Score': 'LNK',
    'Foul Winning Ability Score': 'FWN',
    'Penalties Score': 'PEN',
    'Set Pieces Score': 'SET',
    'Goalkeeper Aggressiveness Score': 'AGG',
    'Goalkeeper Passing Ability Score': 'PAS',
    'Goalkeeper Shot Stopping Score': 'STP'
  };
  return shortNames[attrName] || attrName.substring(0, 3).toUpperCase();
}

/**
 * Attribute mapping for StatsTable sorting and display
 */
export const ATTRIBUTE_MAPPING = {
  // Outfield player attributes (9 key attributes)
  crossing: "Crossing Score",
  dribbling: "Dribbling Score",
  finishing: "Finishing Score",
  creativity: "Creativity Score",
  buildupPassing: "Buildup Passing Score",
  aerialAbility: "Aerial Ability Score",
  defensiveAwareness: "Defensive Awareness Score",
  ballWinning: "Ball Winning Score",
  penalties: "Penalties Score",
  
  // Goalkeeper attributes (all 3)
  shotStopping: "Goalkeeper Shot Stopping Score",
  passing: "Goalkeeper Passing Ability Score", 
  aggressiveness: "Goalkeeper Aggressiveness Score"
};

/**
 * Column definitions for StatsTable - Stats-only view (no bio columns)
 */
export const OUTFIELD_STATS_FIELDS = {
  // Position for player identification
  position: "Pos",
  
  // Name for player identification
  name: "Name",
  
  // 9 key outfield attribute columns
  crossing: "CRO",         // getAttributeShortName("Crossing Score")
  dribbling: "DRI",        // getAttributeShortName("Dribbling Score")
  finishing: "FIN",        // getAttributeShortName("Finishing Score")
  creativity: "CRE",       // getAttributeShortName("Creativity Score") 
  buildupPassing: "BLD",   // getAttributeShortName("Buildup Passing Score")
  aerialAbility: "AER",    // getAttributeShortName("Aerial Ability Score")
  defensiveAwareness: "AWR", // getAttributeShortName("Defensive Awareness Score")
  ballWinning: "WIN",      // getAttributeShortName("Ball Winning Score")
  penalties: "PEN"         // getAttributeShortName("Penalties Score")
};

export const GOALKEEPER_STATS_FIELDS = {
  // Position for player identification
  position: "Pos",
  
  // Name for player identification
  name: "Name",
  
  // All 3 GK attribute columns
  shotStopping: "STP",     // getAttributeShortName("Goalkeeper Shot Stopping Score")
  passing: "PAS",          // getAttributeShortName("Goalkeeper Passing Ability Score")
  aggressiveness: "AGG"    // getAttributeShortName("Goalkeeper Aggressiveness Score")
};

/**
 * Helper function to get attribute value class for color coding
 * @param {number} value - Attribute value
 * @returns {string} CSS class name
 */
export function getAttributeValueClass(value) {
  if (value >= 70) return 'stats-attr-high';
  if (value >= 50) return 'stats-attr-medium'; 
  return 'stats-attr-low';
}

/**
 * Maps nationality names to ISO country codes for flag display
 * @param {string} nationality - Full nationality name  
 * @param {string} nationalityCode - ISO code from database
 * @returns {string} Valid ISO country code
 */
export function getNationalityCode(nationality, nationalityCode) {
  // Return existing code if valid
  if (nationalityCode && nationalityCode.length === 2) {
    return nationalityCode;
  }
  
  // UK nations mapping (England, Scotland, Wales, Northern Ireland → GB)
  const ukNations = {
    "England": "GB",
    "Scotland": "GB", 
    "Wales": "GB",
    "Northern Ireland": "GB"
  };
  
  // Other common mappings
  const nationalityMappings = {
    ...ukNations,
    "United States": "US",
    "Korea Republic": "KR",
    "Korea DPR": "KP"
    // Add more as needed
  };
  
  return nationalityMappings[nationality] || nationality;
}

/**
 * Calculates time difference between two timestamps and returns an object with days, hours, minutes, and seconds
 * @param {number} timeNew - The newer timestamp (in milliseconds)
 * @param {number|string} timestamp - The older timestamp (in milliseconds) or date string
 * @returns {Object} Object containing days, hours, minutes, and seconds
 */
export function timeDelta(timeNew, timestamp) {
  // Convert timestamp to a number if it's a date string
  const time = typeof timestamp === 'string' ? new Date(timestamp).getTime() : timestamp;
  
  const diff = (timeNew - time) >= 0 ? (timeNew - time) : 0;
  const millisecondsInADay = 24 * 60 * 60 * 1000;
  const millisecondsInAnHour = 60 * 60 * 1000;
  const millisecondsInAMinute = 60 * 1000;
  const days = Math.floor(diff / millisecondsInADay);
  const hours = Math.floor((diff % millisecondsInADay) / millisecondsInAnHour);
  const minutes = Math.floor((diff % millisecondsInAnHour) / millisecondsInAMinute);
  const seconds = Math.floor((diff % millisecondsInAMinute) / 1000);

  return {
    'days': days,
    'hours': hours,
    'minutes': minutes,
    'seconds': seconds,
  }
}
